import moment from 'moment';

import { PartitionedPatientBillingEventsRead } from '@headway/api/models/PartitionedPatientBillingEventsRead';
import { PatientBillingEventRead } from '@headway/api/models/PatientBillingEventRead';
import { PatientBillingEventStatus } from '@headway/api/models/PatientBillingEventStatus';

export interface PartitionedBillingEvents {
  past: PatientBillingEventRead[];
  needsAttention: PatientBillingEventRead[];
  upcoming: PatientBillingEventRead[];
}

const hasInvoiceBackfillAndZeroBalance = (
  billingEvent: PatientBillingEventRead
) =>
  billingEvent.doesApptHaveInvoiceBackfill &&
  billingEvent.invoiceBalanceCents === 0;

export const partitionBillingEvents = ({
  billingEvents,
  years,
  types,
}: {
  billingEvents: PatientBillingEventRead[];
  years?: string[];
  types?: string[];
}): PartitionedBillingEvents =>
  billingEvents.reduce(
    (acc, billingEvent) => {
      if (
        (years?.length &&
          !years.includes(
            moment(billingEvent.billingEventDate).year().toString()
          )) ||
        (types?.length && !types.includes(billingEvent.billingEventType))
      ) {
        return acc;
      }
      if (
        [
          PatientBillingEventStatus.CHARGED,
          PatientBillingEventStatus.REFUNDED,
          PatientBillingEventStatus.DISPUTE_WON,
          PatientBillingEventStatus.DISPUTE_LOST,
          PatientBillingEventStatus.DISPUTE_PENDING,
        ].includes(billingEvent.status)
      ) {
        acc.past.push(billingEvent);
      } else if (
        [
          PatientBillingEventStatus.UPCOMING_AUTO_CHARGE,
          PatientBillingEventStatus.SCHEDULED_PAYMENT,
        ].includes(billingEvent.status)
      ) {
        if (!hasInvoiceBackfillAndZeroBalance(billingEvent)) {
          acc.upcoming.push(billingEvent);
        }
      } else if (
        billingEvent.status !==
          PatientBillingEventStatus.PENDING_MANUAL_PAYMENT ||
        !hasInvoiceBackfillAndZeroBalance(billingEvent)
      ) {
        // This is a hack to hide billing events that need attention if the patient invoice is 0
        // One known case is we hide ledger 2.0 backfill events in the BE: https://github.com/headway/headway/blob/b224815017dfeec9d2952277d36202777dfa4a02/mamba/modules/billing/services/patient_billing_event_service.py#L233
        // So it would come out to a non-zero balance in 'amountCents'. Looking at the overall invoice balance, if it's 0 then we just hide it to not
        // show an incorrect balance due for the patient
        acc.needsAttention.push(billingEvent);
      }
      return acc;
    },
    {
      past: [] as PatientBillingEventRead[],
      needsAttention: [] as PatientBillingEventRead[],
      upcoming: [] as PatientBillingEventRead[],
    }
  );

export const filterBillingEvents = ({
  partitionedBillingEvents,
  years = new Set(),
  types = new Set(),
}: {
  partitionedBillingEvents: PartitionedPatientBillingEventsRead;
  years: Set<string>;
  types: Set<string>;
}): PartitionedBillingEvents => {
  if (years.size === 0 && types.size === 0) {
    return partitionedBillingEvents;
  }

  const matchesFilters = (event: PatientBillingEventRead): boolean => {
    const matchesYear =
      years.size === 0 ||
      years.has(moment(event.billingEventDate).year().toString());
    const matchesType = types.size === 0 || types.has(event.billingEventType);
    return matchesYear && matchesType;
  };

  return {
    past: partitionedBillingEvents.past.filter(matchesFilters),
    upcoming: partitionedBillingEvents.upcoming.filter(matchesFilters),
    needsAttention:
      partitionedBillingEvents.needsAttention.filter(matchesFilters),
  };
};
